import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, 
  FileText,
  DollarSign,
  Trash2,
  Eye,
  Send,
  CheckCircle,
  Clock,
  XCircle,
  Printer,
  Download
} from 'lucide-react';
import { getInvoices, createInvoice, updateInvoiceStatus, deleteInvoice, getCleaningTasks, getMaintenanceTasks } from '@/lib/api';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { generateInvoicePDF } from '@/lib/invoicePdf';
import { RecurringInvoicesSection } from './RecurringInvoicesSection';
import { ViewInvoiceDialog } from './invoices/ViewInvoiceDialog';
import ReadReceiptBadge from './invoices/ReadReceiptBadge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  sent: 'bg-blue-100 text-blue-700 border-blue-200',
  paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  overdue: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_ICONS = {
  draft: Clock,
  sent: Send,
  paid: CheckCircle,
  overdue: XCircle,
  cancelled: XCircle,
};

export const InvoicesView = ({ selectedProperty, properties, onRefresh }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [formData, setFormData] = useState({
    property_id: selectedProperty?.id || '',
    client_name: '',
    client_email: '',
    date_from: null,
    date_to: null,
    tax_rate: 0,
    notes: '',
    manager_notes: '',
    include_notes_in_pdf: true,
    payment_instructions: '',
    custom_message: '',
    due_date: null,
  });
  // Work notes candidates fetched from tasks in the selected date range +
  // property. Each item: { id, text, hours, timestamp, source, task_title,
  // selected }
  const [candidateNotes, setCandidateNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getInvoices(selectedProperty?.id);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id || !formData.date_from || !formData.date_to) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const selectedWorkNotes = candidateNotes
        .filter((n) => n.selected)
        .map((n) => ({
          text: n.text,
          hours: n.hours,
          timestamp: n.timestamp,
          source: n.source,
          task_title: n.task_title,
        }));
      await createInvoice({
        ...formData,
        date_from: format(formData.date_from, 'yyyy-MM-dd'),
        date_to: format(formData.date_to, 'yyyy-MM-dd'),
        due_date: formData.due_date ? format(formData.due_date, 'yyyy-MM-dd') : null,
        work_notes: selectedWorkNotes,
      });
      toast.success('Invoice created');
      resetForm();
      setDialogOpen(false);
      fetchInvoices();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const handleStatusChange = async (invoiceId, status) => {
    try {
      const paidDate = status === 'paid' ? format(new Date(), 'yyyy-MM-dd') : null;
      await updateInvoiceStatus(invoiceId, status, paidDate);
      toast.success('Status updated');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!selectedInvoice) return;
    
    try {
      await deleteInvoice(selectedInvoice.id);
      toast.success('Invoice deleted');
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handlePrint = (invoice) => {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const resetForm = () => {
    setFormData({
      property_id: selectedProperty?.id || '',
      client_name: '',
      client_email: '',
      date_from: null,
      date_to: null,
      tax_rate: 0,
      notes: '',
      manager_notes: '',
      include_notes_in_pdf: true,
      payment_instructions: '',
      custom_message: '',
      due_date: null,
    });
    setCandidateNotes([]);
  };

  // Whenever property + both dates are set, fetch candidate work notes.
  useEffect(() => {
    if (!dialogOpen) return;
    const { property_id, date_from, date_to } = formData;
    if (!property_id || !date_from || !date_to) {
      setCandidateNotes([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingNotes(true);
      try {
        const [c, m] = await Promise.all([
          getCleaningTasks(property_id),
          getMaintenanceTasks(property_id),
        ]);
        const from = format(date_from, 'yyyy-MM-dd');
        const to = format(date_to, 'yyyy-MM-dd');
        const collect = (task, source) =>
          (task.work_notes || [])
            .filter((n) => (n.note || '').trim().length > 0)
            .map((n) => ({
              id: `${source}:${task.id}:${n.id || n.timestamp}`,
              text: n.note,
              hours: n.hours || 0,
              timestamp: n.timestamp,
              source,
              task_title:
                source === 'cleaning'
                  ? `Cleaning · ${format(parseISO(task.scheduled_date), 'MMM d')}`
                  : `${task.title || 'Maintenance'} · ${format(parseISO(task.scheduled_date), 'MMM d')}`,
              task_id: task.id,
              selected: true,
            }));
        const cleaningNotes = (c.data || [])
          .filter((t) => t.scheduled_date >= from && t.scheduled_date <= to && t.status === 'completed')
          .flatMap((t) => collect(t, 'cleaning'));
        const maintNotes = (m.data || [])
          .filter((t) => t.scheduled_date >= from && t.scheduled_date <= to && t.status === 'completed')
          .flatMap((t) => collect(t, 'maintenance'));
        if (!cancelled) {
          setCandidateNotes([...cleaningNotes, ...maintNotes].sort(
            (a, b) => (a.timestamp || '').localeCompare(b.timestamp || '')
          ));
        }
      } catch (err) {
        if (!cancelled) setCandidateNotes([]);
      } finally {
        if (!cancelled) setLoadingNotes(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, formData.property_id, formData.date_from, formData.date_to]);

  const toggleCandidateNote = (id) => {
    setCandidateNotes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, selected: !n.selected } : n))
    );
  };

  const handleDownloadPDF = (invoice) => {
    try {
      generateInvoicePDF(invoice);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('PDF generation failed', err);
      toast.error('Failed to generate PDF');
    }
  };

  const totalPending = invoices.filter(i => i.status === 'sent' || i.status === 'overdue')
    .reduce((sum, i) => sum + i.total, 0);
  const totalPaid = invoices.filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + i.total, 0);

  return (
    <div className="space-y-6" data-testid="invoices-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Invoices
          </h1>
          <p className="text-slate-500 mt-1">
            Generate and manage invoices from tracked hours
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="create-invoice-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Invoices</p>
                <p className="text-2xl font-bold text-slate-900 font-manrope">{invoices.length}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-lg">
                <FileText className="h-5 w-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Amount</p>
                <p className="text-2xl font-bold text-amber-600 font-manrope">${totalPending.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Paid Amount</p>
                <p className="text-2xl font-bold text-emerald-600 font-manrope">${totalPaid.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recurring Invoices */}
      <RecurringInvoicesSection
        properties={properties}
        onInvoiceGenerated={fetchInvoices}
      />

      {/* Invoices List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : invoices.length === 0 ? (
        <Card className="bg-white border border-slate-100">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No invoices yet</h3>
            <p className="text-slate-500 mb-4">Create an invoice from completed tasks</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {invoices.map(invoice => {
            const StatusIcon = STATUS_ICONS[invoice.status];
            return (
              <Card 
                key={invoice.id}
                className="bg-white border border-slate-100 card-hover"
                data-testid={`invoice-card-${invoice.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900 font-manrope">
                          {invoice.invoice_number}
                        </h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border flex items-center gap-1 ${STATUS_COLORS[invoice.status]}`}>
                          <StatusIcon className="h-3 w-3" />
                          {invoice.status}
                        </span>
                        {invoice.auto_generated && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-100"
                            title="Auto-generated by recurring rule"
                            data-testid={`auto-badge-${invoice.id}`}
                          >
                            Auto
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-slate-600 mb-2">
                        {invoice.property_name}
                        {invoice.client_name && ` • ${invoice.client_name}`}
                      </p>

                      <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                        <span>Period: {format(parseISO(invoice.date_from), 'MMM d')} - {format(parseISO(invoice.date_to), 'MMM d, yyyy')}</span>
                        <span>{invoice.line_items?.length || 0} items</span>
                        <span>
                          {(invoice.line_items || []).reduce((s, i) => s + (Number(i.hours) || 0), 0).toFixed(1)}h total
                        </span>
                        {invoice.due_date && (
                          <span>Due: {format(parseISO(invoice.due_date), 'MMM d, yyyy')}</span>
                        )}
                        <ReadReceiptBadge invoice={invoice} />
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-slate-900 font-manrope">
                          ${invoice.total.toFixed(2)}
                        </p>
                        {invoice.tax_amount > 0 && (
                          <p className="text-xs text-slate-500">
                            (incl. ${invoice.tax_amount.toFixed(2)} tax)
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedInvoice(invoice); setViewDialogOpen(true); }}
                          data-testid={`view-invoice-${invoice.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePrint(invoice)}
                          data-testid={`print-invoice-${invoice.id}`}
                        >
                          <Printer className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPDF(invoice)}
                          data-testid={`download-invoice-pdf-${invoice.id}`}
                          title="Download PDF"
                        >
                          <Download className="h-4 w-4" />
                        </Button>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className={STATUS_COLORS[invoice.status]}>
                              Status
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'draft')}>
                              <Clock className="h-4 w-4 mr-2" /> Draft
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'sent')}>
                              <Send className="h-4 w-4 mr-2" /> Sent
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'paid')}>
                              <CheckCircle className="h-4 w-4 mr-2" /> Paid
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'overdue')}>
                              <XCircle className="h-4 w-4 mr-2" /> Overdue
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedInvoice(invoice); setDeleteDialogOpen(true); }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-invoice-${invoice.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={dialogOpen} onOpenChange={() => { resetForm(); setDialogOpen(false); }}>
        <DialogContent
          className="w-[calc(100vw-1rem)] max-w-[95vw] sm:max-w-[560px] max-h-[90dvh] p-0 gap-0 flex flex-col overflow-hidden"
          data-testid="create-invoice-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 flex-shrink-0 border-b">
            <DialogTitle className="font-manrope">Create Invoice</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreate} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label>Property *</Label>
              <Select
                value={formData.property_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, property_id: value }))}
              >
                <SelectTrigger data-testid="invoice-property-select">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Period Start *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start", !formData.date_from && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date_from ? format(formData.date_from, 'MMM d, yyyy') : 'Select'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={formData.date_from} onSelect={(d) => setFormData(prev => ({ ...prev, date_from: d }))} />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Period End *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start", !formData.date_to && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date_to ? format(formData.date_to, 'MMM d, yyyy') : 'Select'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={formData.date_to} onSelect={(d) => setFormData(prev => ({ ...prev, date_to: d }))} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Name</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                  placeholder="Property Owner"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Email</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                  placeholder="owner@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start", !formData.due_date && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.due_date ? format(formData.due_date, 'MMM d') : 'Select'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={formData.due_date} onSelect={(d) => setFormData(prev => ({ ...prev, due_date: d }))} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Manager Notes (to the client, appears above line items on PDF) */}
            <div className="space-y-2">
              <Label htmlFor="invoice-manager-notes">Message to client (optional)</Label>
              <Textarea
                id="invoice-manager-notes"
                value={formData.manager_notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, manager_notes: e.target.value }))}
                placeholder="Thanks for the work this month! Let us know if you have any questions…"
                rows={2}
                data-testid="invoice-manager-notes-input"
              />
            </div>

            {/* Work Notes picker */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Include work notes on invoice</Label>
                {candidateNotes.length > 0 && (
                  <div className="flex gap-2 text-xs">
                    <button
                      type="button"
                      className="text-slate-500 hover:text-slate-900"
                      onClick={() =>
                        setCandidateNotes((p) => p.map((n) => ({ ...n, selected: true })))
                      }
                      data-testid="invoice-work-notes-select-all"
                    >
                      Select all
                    </button>
                    <span className="text-slate-300">·</span>
                    <button
                      type="button"
                      className="text-slate-500 hover:text-slate-900"
                      onClick={() =>
                        setCandidateNotes((p) => p.map((n) => ({ ...n, selected: false })))
                      }
                      data-testid="invoice-work-notes-clear"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <div className="rounded-md border border-slate-200 max-h-56 overflow-y-auto">
                {(!formData.property_id || !formData.date_from || !formData.date_to) ? (
                  <p className="text-xs text-slate-500 p-3">
                    Pick a property and a date range first — we'll show every work note logged in
                    that window so you can pick.
                  </p>
                ) : loadingNotes ? (
                  <p className="text-xs text-slate-500 p-3">Loading work notes…</p>
                ) : candidateNotes.length === 0 ? (
                  <p className="text-xs text-slate-500 p-3">
                    No work notes were logged on completed tasks in this range.
                  </p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {candidateNotes.map((n) => (
                      <li key={n.id} className="p-2.5 flex items-start gap-2.5">
                        <input
                          type="checkbox"
                          checked={n.selected}
                          onChange={() => toggleCandidateNote(n.id)}
                          className="mt-1 accent-slate-900"
                          data-testid={`invoice-work-note-toggle-${n.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${
                              n.source === 'cleaning'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}>
                              {n.source}
                            </span>
                            <span className="text-xs text-slate-600 truncate">{n.task_title}</span>
                            {n.hours > 0 && (
                              <span className="text-[10px] text-slate-500 ml-auto">{n.hours}h</span>
                            )}
                          </div>
                          <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{n.text}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (internal reference)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                rows={2}
                data-testid="invoice-notes-input"
              />
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <div className="text-xs text-slate-600">
                  Include notes on downloaded PDF
                </div>
                <Switch
                  checked={formData.include_notes_in_pdf}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, include_notes_in_pdf: v }))}
                  data-testid="invoice-include-notes-toggle"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Message on PDF (optional)</Label>
              <Textarea
                value={formData.custom_message}
                onChange={(e) => setFormData(prev => ({ ...prev, custom_message: e.target.value }))}
                placeholder="Thank you for your business!"
                rows={2}
                data-testid="invoice-custom-message-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Instructions (optional)</Label>
              <Textarea
                value={formData.payment_instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_instructions: e.target.value }))}
                placeholder="Wire to Bank of X, Acct #12345, Routing #67890..."
                rows={2}
                data-testid="invoice-payment-instructions-input"
              />
            </div>

            <p className="text-xs text-slate-500">
              Invoice will include all completed cleaning and maintenance tasks within the selected period.
            </p>
            </div>

            <DialogFooter className="px-6 py-3 border-t bg-white flex-shrink-0">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setDialogOpen(false); }}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-invoice-btn">
                Generate Invoice
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <ViewInvoiceDialog
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        invoice={selectedInvoice}
        onDownloadPDF={handleDownloadPDF}
        onInvoiceSent={() => fetchInvoices()}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice &ldquo;{selectedInvoice?.invoice_number}&rdquo;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvoicesView;
